/**
 * Client-side image downscale — runs entirely in the browser via canvas.
 * Anything bigger than `maxLongEdge` px on the long edge is scaled down
 * before upload, keeping aspect ratio. Non-image files (PDFs, etc.) and
 * already-small images are returned untouched.
 *
 * Honours EXIF orientation via `createImageBitmap({ imageOrientation: 'from-image' })`
 * when available, so iPhone photos taken sideways arrive upright.
 *
 * @param {File} file
 * @param {{ maxLongEdge?: number, quality?: number }} [opts]
 * @returns {Promise<File>} a resized File (or the original if no resize needed)
 */
export async function downscaleImage(file, opts = {}) {
  const { maxLongEdge = 1600, quality = 0.82 } = opts;

  if (!file || !file.type || !file.type.startsWith('image/')) return file;
  // HEIC needs server-side conversion; the browser can't decode it on canvas.
  // Let it pass through and the backend resize step will handle it.
  if (file.type === 'image/heic' || file.type === 'image/heif') return file;

  let bitmap;
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch {
    // Fallback for browsers that don't support imageOrientation option
    try { bitmap = await createImageBitmap(file); } catch { return file; }
  }

  const longEdge = Math.max(bitmap.width, bitmap.height);
  if (longEdge <= maxLongEdge) {
    bitmap.close?.();
    return file;
  }

  const scale = maxLongEdge / longEdge;
  const w = Math.round(bitmap.width * scale);
  const h = Math.round(bitmap.height * scale);

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(bitmap, 0, 0, w, h);
  bitmap.close?.();

  const blob = await new Promise(resolve =>
    canvas.toBlob(resolve, 'image/jpeg', quality)
  );
  if (!blob) return file;

  // Reuse original filename but force .jpg extension for clarity
  const baseName = (file.name || 'image').replace(/\.[^.]+$/, '');
  return new File([blob], `${baseName}.jpg`, { type: 'image/jpeg', lastModified: Date.now() });
}
